import React, { useState, useEffect, useRef } from 'react';
import { formStyles } from '../../styles/formStyles';
import { API_URL } from '../../utils/constants';

const RecordInputForm = ({ token, onSaveSuccess }) => {
  const initialState = {
    customer_id: null, // 기존 고객 ID
    company_name: '', manager: '', phone: '', mobile: '', address: '',
    receipt_date: new Date().toISOString().split('T')[0],
    receipt_details: '', process_details: '', total_cost: '', progress_status: '진행', notes: ''
  };

  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSearchTab, setActiveSearchTab] = useState('customer'); // 'customer' 또는 'stock'
  
  // 고객 검색 관련 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const companyNameRef = useRef(null);
  const receiptDetailsRef = useRef(null);

  // 재고 검색 관련 상태
  const [stockCategory, setStockCategory] = useState('');
  const [stockSearchTerm, setStockSearchTerm] = useState('');
  const [stockResults, setStockResults] = useState([]);
  const [showStockResults, setShowStockResults] = useState(false);
  const [selectedStockIndex, setSelectedStockIndex] = useState(-1);
  const [selectedStocks, setSelectedStocks] = useState([]); // 선택된 부품 목록
  const [stockQuantity, setStockQuantity] = useState(1); // 수량
  const stockSearchRef = useRef(null);

  // 매장판매 관련 상태
  const [laborCost, setLaborCost] = useState(''); // 공임
  const [vatIncluded, setVatIncluded] = useState(false); // 부가세 포함 여부
  const [salePayment, setSalePayment] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: '',
    paymentMethod: '',
    taxInvoice: ''
  });
  const [isSubmittingSale, setIsSubmittingSale] = useState(false);

  // 항목 목록
  const CATEGORIES = [
    'CPU', '쿨러/튜닝', '메인보드', '메모리', '그래픽카드',
    'SSD', 'HDD', '케이스', '파워', '소프트웨어',
    '모니터', '키보드', '마우스', '사운드바', '스피커',
    'PC헤드셋', '공유기/무선랜', 'IP공유기/허브', '프린터/복합기', '케이블',
    '컨트롤러', '멀티탭', 'ODD', '외장HDD/SSD', 'USB', '기타'
  ];

  // 고객 검색 API 호출
  useEffect(() => {
    const performSearch = async () => {
      // 검색 조건: 2글자 이상 또는 숫자 4자리 이상
      const onlyNumbers = searchTerm.replace(/[^0-9]/g, '');
      const hasNumbers = onlyNumbers.length >= 4;
      const hasText = searchTerm.length >= 2;

      if (!hasNumbers && !hasText) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/customers/search?term=${encodeURIComponent(searchTerm)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data);
          setShowResults(data.length > 0);
          setSelectedIndex(-1);
        }
      } catch (err) {
        console.error('검색 오류:', err);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, token]);

  // 재고 검색 API 호출
  useEffect(() => {
    const performStockSearch = async () => {
      if (!stockCategory) {
        setStockResults([]);
        setShowStockResults(false);
        return;
      }

      try {
        let url = `${API_URL}/materials/available?category=${encodeURIComponent(stockCategory)}`;
        if (stockSearchTerm.length >= 1) {
          url += `&term=${encodeURIComponent(stockSearchTerm)}`;
        }
        
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setStockResults(data);
          setShowStockResults(data.length > 0);
          setSelectedStockIndex(-1);
        }
      } catch (err) {
        console.error('재고 검색 오류:', err);
      }
    };

    const debounceTimer = setTimeout(performStockSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [stockCategory, stockSearchTerm, token]);

  // 고객 선택 처리
  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id, // 기존 고객 ID 저장
      company_name: customer.company_name || '',
      manager: customer.manager || '',
      phone: customer.phone || '',
      mobile: customer.mobile || '',
      address: customer.address || ''
    }));
    setSearchTerm('');
    setShowResults(false);
    setSearchResults([]);
    
    // 고객 선택 후 접수내역으로 포커스 이동
    setTimeout(() => {
      receiptDetailsRef.current?.focus();
    }, 100);
  };

  // 재고 선택 처리 - 목록에 추가
  const handleStockSelect = (stock) => {
    const qty = parseInt(stockQuantity) || 1;
    if (qty > stock.total_quantity) {
      alert(`재고가 부족합니다. (현재 재고: ${stock.total_quantity}개)`);
      return;
    }
    
    // 이미 선택된 부품인지 확인
    const existingIndex = selectedStocks.findIndex(
      s => s.category === stock.category && s.product_name === stock.product_name
    );
    
    if (existingIndex >= 0) {
      // 이미 있으면 수량 추가
      const newStocks = [...selectedStocks];
      const newQty = newStocks[existingIndex].quantity + qty;
      if (newQty > stock.total_quantity) {
        alert(`재고가 부족합니다. (현재 재고: ${stock.total_quantity}개)`);
        return;
      }
      newStocks[existingIndex].quantity = newQty;
      setSelectedStocks(newStocks);
    } else {
      // 새로 추가
      setSelectedStocks(prev => [...prev, {
        ...stock,
        quantity: qty
      }]);
    }
    
    // 드롭다운 닫고 항목 선택 초기화
    setShowStockResults(false);
    setStockSearchTerm('');
    setStockCategory('');
    setStockQuantity(1);
  };

  // 선택된 부품 삭제
  const handleRemoveStock = (index) => {
    setSelectedStocks(prev => prev.filter((_, i) => i !== index));
  };

  // 선택된 부품 총액 계산
  const totalPartsCost = selectedStocks.reduce((sum, stock) => 
    sum + (parseFloat(stock.selling_price || 0) * stock.quantity), 0
  );

  // 전체비용 계산 (부품 + 공임)
  const totalCost = totalPartsCost + (parseFloat(laborCost) || 0);
  
  // 부가세 계산
  const vatAmount = vatIncluded ? Math.floor(totalCost * 0.1) : 0;
  const totalWithVat = totalCost + vatAmount;

  // 금액 포맷
  const formatCurrency = (value) => {
    if (!value) return '-';
    return Number(value).toLocaleString() + '원';
  };

  // 키보드 네비게이션
  const handleKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < searchResults.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0) {
        handleCustomerSelect(searchResults[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowResults(false);
    }
  };

  // 재고 검색 키보드 네비게이션
  const handleStockKeyDown = (e) => {
    if (!showStockResults || stockResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedStockIndex(prev => 
        prev < stockResults.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedStockIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedStockIndex >= 0) {
        handleStockSelect(stockResults[selectedStockIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowStockResults(false);
    }
  };

  // F2, F3, F4 키 단축키
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        setActiveSearchTab('customer');
        setTimeout(() => searchInputRef.current?.focus(), 50);
      } else if (e.key === 'F3') {
        e.preventDefault();
        companyNameRef.current?.focus();
      } else if (e.key === 'F4') {
        e.preventDefault();
        setActiveSearchTab('stock');
        setTimeout(() => stockSearchRef.current?.focus(), 50);
      }
    };
    
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // 주소 검색 팝업 열기
  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: function(data) {
        // 도로명 주소 또는 지번 주소 선택
        const addr = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
        
        // 주소 입력란에 자동 입력
        setFormData(prev => ({ ...prev, address: addr }));
      }
    }).open();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // 전화번호/휴대폰 자동 하이픈
    if (name === 'phone' || name === 'mobile') {
      const numValue = value.replace(/[^0-9]/g, '');
      let formatted = numValue;
      if (numValue.length > 3 && numValue.length <= 7) {
        formatted = numValue.replace(/(\d{3})(\d{1,4})/, '$1-$2');
      } else if (numValue.length > 7) {
        formatted = numValue.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
      }
      setFormData(prev => ({ ...prev, [name]: formatted }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 매장판매 처리
  const handleStoreSale = async () => {
    if (selectedStocks.length === 0) {
      alert('선택된 부품이 없습니다.');
      return;
    }

    setIsSubmittingSale(true);
    try {
      // 1. '방문고객' 고객 찾기 또는 생성
      let customerId;
      const searchResponse = await fetch(`${API_URL}/customers/search?term=${encodeURIComponent('방문고객')}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (searchResponse.ok) {
        const customers = await searchResponse.json();
        const visitCustomer = customers.find(c => c.company_name === '방문고객');
        
        if (visitCustomer) {
          customerId = visitCustomer.id;
        } else {
          // 방문고객 생성
          const createResponse = await fetch(`${API_URL}/customers`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ companyName: '방문고객' })
          });
          if (!createResponse.ok) throw new Error('고객 생성 실패');
          const newCustomer = await createResponse.json();
          customerId = newCustomer.id;
        }
      }

      // 2. 작업 레코드 생성
      const receiptDetails = '매장판매\n' + selectedStocks.map(s => 
        `[${s.category}] ${s.product_name} x${s.quantity}`
      ).join('\n');

      const recordResponse = await fetch(`${API_URL}/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customerId: customerId,
          receiptDate: new Date().toISOString().split('T')[0],
          receiptDetails: receiptDetails,
          totalCost: laborCost || 0,
          progressStatus: '완결',
          completionDate: new Date().toISOString().split('T')[0],
          vatIncluded: vatIncluded
        })
      });

      if (!recordResponse.ok) throw new Error('작업 정보 저장 실패');
      const newRecord = await recordResponse.json();

      // 3. 자재 사용 처리 (FIFO)
      for (const stock of selectedStocks) {
        const useResponse = await fetch(`${API_URL}/materials/use`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            record_id: newRecord.id,
            category: stock.category,
            product_name: stock.product_name,
            quantity: stock.quantity,
            recipient: '방문고객'
          })
        });
        if (!useResponse.ok) {
          console.error('자재 사용 처리 실패:', stock.product_name);
        }
      }

      // 4. 결제 정보 저장 (금액이 있는 경우)
      const paymentAmount = salePayment.amount || totalWithVat;
      if (paymentAmount > 0) {
        const paymentResponse = await fetch(`${API_URL}/payments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            recordId: newRecord.id,
            paymentDate: salePayment.paymentDate,
            amount: paymentAmount,
            paymentMethod: salePayment.paymentMethod || '현금',
            taxInvoice: salePayment.taxInvoice
          })
        });
        if (!paymentResponse.ok) {
          console.error('결제 정보 저장 실패');
        }
      }

      alert('매장판매가 등록되었습니다.');
      
      // 초기화
      setSelectedStocks([]);
      setLaborCost('');
      setVatIncluded(false);
      setSalePayment({
        paymentDate: new Date().toISOString().split('T')[0],
        amount: '',
        paymentMethod: '',
        taxInvoice: ''
      });
      setStockCategory('');
      setStockSearchTerm('');
      onSaveSuccess();
      
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSubmittingSale(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name || !formData.receipt_details) {
      alert("업체명과 접수내역은 필수입니다.");
      return;
    }

    setIsSubmitting(true);
    try {
      let customerId = formData.customer_id;

      // 기존 고객이 없으면 새로 생성
      if (!customerId) {
        const customerResponse = await fetch(`${API_URL}/customers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            companyName: formData.company_name,
            manager: formData.manager,
            phone: formData.phone,
            mobile: formData.mobile,
            address: formData.address
          })
        });

        if (!customerResponse.ok) throw new Error('고객 정보 저장 실패');
        
        const customer = await customerResponse.json();
        customerId = customer.id;
      }

      // 작업 정보 저장
      const recordResponse = await fetch(`${API_URL}/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customerId: customerId,
          receiptDate: formData.receipt_date,
          receiptDetails: formData.receipt_details,
          processDetails: formData.process_details,
          totalCost: formData.total_cost,
          progressStatus: formData.progress_status,
          notes: formData.notes
        })
      });

      if (!recordResponse.ok) throw new Error('작업 정보 저장 실패');

      alert("저장되었습니다.");
      setFormData(initialState);
      setSearchTerm('');
      onSaveSuccess();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <aside style={formStyles.formContainer}>
      {/* 검색 탭 (고객/재고) */}
      <div style={{ marginBottom: '17px' }}>
        {/* 탭 버튼 */}
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button
            type="button"
            onClick={() => setActiveSearchTab('customer')}
            style={{
              padding: '10px 20px',
              border: '1px solid #3498db',
              borderBottom: 'none',
              borderRadius: '6px 6px 0 0',
              backgroundColor: activeSearchTab === 'customer' ? '#e8f4fc' : '#fff',
              color: '#3498db',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            고객 검색 (F2)
          </button>
          <button
            type="button"
            onClick={() => setActiveSearchTab('stock')}
            style={{
              padding: '10px 20px',
              border: '1px solid #27ae60',
              borderBottom: 'none',
              borderRadius: '6px 6px 0 0',
              marginLeft: '4px',
              backgroundColor: activeSearchTab === 'stock' ? '#e8f8f0' : '#fff',
              color: '#27ae60',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            재고 검색 (F4)
          </button>
          {/* 탭 오른쪽 연장선 */}
          <div style={{ 
            flex: 1, 
            borderBottom: `1px solid ${activeSearchTab === 'customer' ? '#3498db' : '#27ae60'}`,
            alignSelf: 'flex-end',
            height: '1px'
          }} />
        </div>

        {/* 탭 아래 구분선 + 컨텐츠 영역 */}
        <div style={{
          borderTop: `1px solid ${activeSearchTab === 'customer' ? '#3498db' : '#27ae60'}`,
          paddingTop: '15px',
          backgroundColor: activeSearchTab === 'customer' ? '#e8f4fc' : '#e8f8f0'
        }}>
          {/* 고객 검색 탭 내용 */}
          {activeSearchTab === 'customer' && (
            <div style={{ display: 'flex', gap: '8.5px', position: 'relative' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input 
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="업체명/담당자 (2글자 이상) 또는 전화번호 (4자리)"
                  style={{
                    ...formStyles.input,
                    width: '100%',
                    padding: '10.2px',
                    fontSize: '15px',
                    border: '2px solid #3498db'
                  }}
                />
                
                {/* 검색 결과 드롭다운 */}
                {showResults && searchResults.length > 0 && (
                  <div style={formStyles.searchResultsDropdown}>
                    {searchResults.map((customer, index) => (
                      <div
                        key={customer.id}
                        onClick={() => handleCustomerSelect(customer)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        style={{
                          ...formStyles.searchResultItem,
                          backgroundColor: selectedIndex === index ? '#e3f2fd' : 'white'
                        }}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                          {customer.company_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {customer.manager && `담당: ${customer.manager}`}
                          {customer.manager && (customer.phone || customer.mobile) && ' | '}
                          {customer.phone && `☎ ${customer.phone}`}
                          {customer.phone && customer.mobile && ' / '}
                          {customer.mobile && `📱 ${customer.mobile}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    customer_id: null,
                    company_name: '',
                    manager: '',
                    phone: '',
                    mobile: '',
                    address: ''
                  }));
                  setSearchTerm('');
                  setShowResults(false);
                  setSearchResults([]);
                }}
                style={{
                  padding: '10.2px 16px',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                초기화
              </button>
            </div>
          )}

          {/* 재고 검색 탭 내용 */}
          {activeSearchTab === 'stock' && (
            <div>
              {/* 항목 선택 + 수량 (첫 번째 줄) */}
              <div style={{ display: 'flex', gap: '8.5px', marginBottom: '8px' }}>
                <select
                  value={stockCategory}
                  onChange={(e) => {
                    setStockCategory(e.target.value);
                    setStockSearchTerm('');
                  }}
                  style={{
                    ...formStyles.input,
                    flex: 1,
                    padding: '10.2px',
                    fontSize: '14px',
                    border: '2px solid #27ae60',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">-- 항목 선택 --</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  value={stockQuantity}
                  onChange={(e) => setStockQuantity(e.target.value)}
                  style={{
                    ...formStyles.input,
                    width: '70px',
                    padding: '10.2px',
                    fontSize: '14px',
                    border: '2px solid #27ae60',
                    textAlign: 'center'
                  }}
                  placeholder="수량"
                />
                <button
                  type="button"
                  onClick={() => {
                    setStockCategory('');
                    setStockSearchTerm('');
                    setStockResults([]);
                    setShowStockResults(false);
                    setSelectedStocks([]);
                    setLaborCost('');
                    setSalePayment({
                      paymentDate: new Date().toISOString().split('T')[0],
                      amount: '',
                      paymentMethod: '',
                      taxInvoice: ''
                    });
                  }}
                  style={{
                    padding: '10.2px 16px',
                    backgroundColor: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  초기화
                </button>
              </div>
              
              {/* 제품명 검색 (두 번째 줄) */}
              <div style={{ position: 'relative' }}>
                <input
                  ref={stockSearchRef}
                  type="text"
                  value={stockSearchTerm}
                  onChange={(e) => setStockSearchTerm(e.target.value)}
                  onKeyDown={handleStockKeyDown}
                  onFocus={() => stockCategory && stockResults.length > 0 && setShowStockResults(true)}
                  placeholder={stockCategory ? "제품명 검색..." : "항목을 먼저 선택하세요"}
                  disabled={!stockCategory}
                  style={{
                    ...formStyles.input,
                    width: '100%',
                    padding: '10.2px',
                    fontSize: '14px',
                    border: '2px solid #27ae60',
                    backgroundColor: stockCategory ? '#fff' : '#f5f5f5'
                  }}
                />
                
                {/* 재고 검색 결과 드롭다운 */}
                {showStockResults && stockResults.length > 0 && (
                  <div style={{
                    ...formStyles.searchResultsDropdown,
                    maxHeight: '200px'
                  }}>
                    {stockResults.map((stock, index) => (
                      <div
                        key={`${stock.category}-${stock.product_name}`}
                        onClick={() => handleStockSelect(stock)}
                        onMouseEnter={() => setSelectedStockIndex(index)}
                        style={{
                          ...formStyles.searchResultItem,
                          backgroundColor: selectedStockIndex === index ? '#e8f8f0' : 'white',
                          padding: '10px 12px'
                        }}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                          {stock.product_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
                          <span>재고: {stock.total_quantity}개</span>
                          <span>개별 단가: {formatCurrency(stock.selling_price)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* 선택된 부품 목록 */}
              {selectedStocks.length > 0 && (
                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #27ae60',
                  borderRadius: '6px',
                  padding: '12px',
                  marginTop: '10px'
                }}>
                  <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '8px', color: '#27ae60' }}>
                    선택된 부품 ({selectedStocks.length}건)
                  </div>
                  {selectedStocks.map((stock, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: index < selectedStocks.length - 1 ? '1px dashed #ddd' : 'none'
                    }}>
                      <div>
                        <span style={{ fontSize: '12px', color: '#27ae60' }}>[{stock.category}]</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', marginLeft: '4px' }}>{stock.product_name}</span>
                        <span style={{ fontSize: '13px', color: '#666', marginLeft: '8px' }}>x{stock.quantity}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
                          {formatCurrency(stock.selling_price * stock.quantity)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveStock(index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#e74c3c',
                            fontSize: '16px',
                            cursor: 'pointer',
                            padding: '2px 6px'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 재고 검색 탭일 때 매장판매 폼 표시 */}
      {activeSearchTab === 'stock' && (
        <div style={{ marginTop: '15px' }}>
          {/* 공임 및 전체비용 */}
          <div style={{ ...formStyles.formSectionTitle, marginTop: '10px' }}>비용 정보</div>
          <div style={{ display: 'flex', gap: '8.5px', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>공임</label>
              <input
                type="text"
                value={laborCost ? Number(laborCost).toLocaleString() : ''}
                onChange={(e) => setLaborCost(e.target.value.replace(/[^\d]/g, ''))}
                style={formStyles.input}
                placeholder="0"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>전체비용</label>
              <div style={{
                ...formStyles.input,
                backgroundColor: '#e8f5e9',
                fontWeight: '700',
                color: '#27ae60',
                display: 'flex',
                alignItems: 'center'
              }}>
                {formatCurrency(totalPartsCost)} + {formatCurrency(parseFloat(laborCost) || 0)} = {formatCurrency(totalCost)}
              </div>
            </div>
          </div>

          {/* 결제 정보 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', marginBottom: '10px' }}>
            <div style={formStyles.formSectionTitle}>결제 정보</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <span style={{ fontSize: '13px', color: '#7f8c8d', fontWeight: '600' }}>부가세</span>
              <div 
                onClick={() => setVatIncluded(!vatIncluded)}
                style={{
                  width: '44px',
                  height: '24px',
                  backgroundColor: vatIncluded ? '#27ae60' : '#bdc3c7',
                  borderRadius: '12px',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: vatIncluded ? '22px' : '2px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
              </div>
            </label>
          </div>
          
          {/* 부가세 포함 시 금액 상세 표시 */}
          {vatIncluded && (
            <div style={{
              backgroundColor: '#e8f5e9',
              border: '1px solid #27ae60',
              borderRadius: '6px',
              padding: '10px 12px',
              marginBottom: '12px',
              fontSize: '14px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#666' }}>공급가액:</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(totalCost)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#666' }}>부가세 (10%):</span>
                <span style={{ fontWeight: '600', color: '#3498db' }}>{formatCurrency(vatAmount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '6px', borderTop: '1px dashed #27ae60' }}>
                <span style={{ color: '#27ae60', fontWeight: '700' }}>합계:</span>
                <span style={{ fontWeight: '700', color: '#27ae60', fontSize: '16px' }}>{formatCurrency(totalWithVat)}</span>
              </div>
            </div>
          )}
          
          {/* 입력 필드 순서 변경 및 계산서 옵션 추가 */}
          <div style={{ display: 'flex', gap: '8.5px', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>결제일</label>
              <input
                type="date"
                value={salePayment.paymentDate}
                onChange={(e) => setSalePayment(prev => ({ ...prev, paymentDate: e.target.value }))}
                style={formStyles.input}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>결제방식</label>
              <select
                value={salePayment.paymentMethod}
                onChange={(e) => setSalePayment(prev => ({ ...prev, paymentMethod: e.target.value }))}
                style={formStyles.input}
              >
                <option value="">선택하세요</option>
                <option value="현금">현금</option>
                <option value="카드">카드</option>
                <option value="통장">통장</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8.5px', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>계산서</label>
              <select
                value={salePayment.taxInvoice}
                onChange={(e) => {
                  const val = e.target.value;
                  setSalePayment(prev => ({ ...prev, taxInvoice: val }));
                  if (val) setVatIncluded(true);
                }}
                style={formStyles.input}
              >
                <option value="">선택하세요</option>
                <option value="발행">발행</option>
                <option value="현금">현금</option>
                <option value="카드">카드</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>결제금액</label>
              <input
                type="text"
                value={salePayment.amount ? Number(salePayment.amount).toLocaleString() : ''}
                onChange={(e) => setSalePayment(prev => ({ ...prev, amount: e.target.value.replace(/[^\d]/g, '') }))}
                style={formStyles.input}
                placeholder={formatCurrency(totalWithVat)}
              />
            </div>
          </div>

          {/* 매장판매 버튼 */}
          <button
            type="button"
            onClick={handleStoreSale}
            disabled={isSubmittingSale || selectedStocks.length === 0}
            style={{
              ...formStyles.submitButton,
              backgroundColor: isSubmittingSale || selectedStocks.length === 0 ? '#95a5a6' : '#27ae60',
              marginTop: '10px'
            }}
          >
            {isSubmittingSale ? '처리 중...' : '매장판매'}
          </button>
        </div>
      )}

      {/* 고객 검색 탭일 때만 폼 표시 */}
      {activeSearchTab === 'customer' && (
        <form onSubmit={handleSubmit}>
          {/* 고객 정보 */}
          <div style={formStyles.formSectionTitle}>고객 정보</div>
          
          {/* 1행: 업체명, 담당자 */}
          <div style={{ display: 'flex', gap: '8.5px', marginBottom: '12.75px' }}>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>업체명 (F3)</label>
              <input 
                ref={companyNameRef}
                name="company_name" 
                style={formStyles.input} 
                value={formData.company_name} 
                onChange={handleInputChange} 
                placeholder="업체명"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>담당자</label>
              <input 
                name="manager" 
                style={formStyles.input} 
                value={formData.manager} 
                onChange={handleInputChange} 
                placeholder="담당자명"
              />
            </div>
          </div>

          {/* 2행: 전화번호, 휴대폰번호 */}
          <div style={{ display: 'flex', gap: '8.5px', marginBottom: '12.75px' }}>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>전화번호</label>
              <input 
                name="phone" 
                style={formStyles.input} 
                value={formData.phone} 
                onChange={handleInputChange}
                placeholder="전화번호"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>휴대폰번호</label>
              <input 
                name="mobile" 
                style={formStyles.input} 
                value={formData.mobile} 
                onChange={handleInputChange}
                placeholder="휴대폰번호"
              />
            </div>
          </div>

          {/* 3행: 주소 (전체) */}
          <div style={formStyles.inputGroup}>
            <label style={formStyles.label}>주소</label>
            <div style={{ display: 'flex', gap: '8.5px' }}>
              <input 
                name="address" 
                style={{ ...formStyles.input, flex: 1 }} 
                value={formData.address} 
                onChange={handleInputChange}
                placeholder="주소"
              />
              <button
                type="button"
                onClick={handleAddressSearch}
                style={{
                  padding: '10.2px 16px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                주소 검색
              </button>
            </div>
          </div>

          {/* 작업 정보 */}
          <div style={{ ...formStyles.formSectionTitle, marginTop: '21.25px' }}>작업 정보</div>
          
          {/* 접수내역 (큰 입력란) */}
          <div style={formStyles.inputGroup}>
            <label style={formStyles.label}>접수내역 *</label>
            <textarea 
              ref={receiptDetailsRef}
              name="receipt_details" 
              style={{ ...formStyles.input, height: '153px', resize: 'vertical' }} 
              value={formData.receipt_details} 
              onChange={handleInputChange}
              placeholder="접수내역 입력"
            />
          </div>

          <div style={{ display: 'flex', gap: '8.5px', marginBottom: '12.75px' }}>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>금액 (원)</label>
              <input 
                type="number" 
                name="total_cost" 
                style={formStyles.input} 
                value={formData.total_cost} 
                onChange={handleInputChange}
                placeholder="금액"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={formStyles.label}>진행상태</label>
              <select
                name="progress_status"
                value={formData.progress_status}
                onChange={handleInputChange}
                style={formStyles.input}
              >
                <option value="진행">진행</option>
                <option value="완결">완결</option>
                <option value="미수">미수</option>
                <option value="취소">취소</option>
                <option value="대기">대기</option>
              </select>
            </div>
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting}
            style={{ 
              ...formStyles.submitButton, 
              backgroundColor: isSubmitting ? '#95a5a6' : '#2ecc71'
            }}
          >
            {isSubmitting ? '저장 중...' : '데이터 저장'}
          </button>
        </form>
      )}
    </aside>
  );
};

export default RecordInputForm;
